/**
 * TOSS Web Application - useResponsive Hook
 *
 * Custom hook for handling responsive design based on TOSS breakpoints.
 * Provides reactive state for current viewport size and device detection.
 *
 * Breakpoints (from tailwind.config.js):
 * - mobile: max 549px
 * - desktop: 550px+
 * - tablet: 768px+
 * - laptop: 1024px+
 * - wide: 1280px+
 *
 * Per PROJECT_DOCUMENTATION.md Section 9:
 * - Web < 550px: Mobile UI
 * - Web >= 550px: Desktop UI
 */

import { useState, useEffect, useCallback } from 'react';

// ============================================================================
// Constants
// ============================================================================

/**
 * Breakpoint values in pixels
 * Matches tailwind.config.js configuration
 */
export const BREAKPOINTS = {
  // Standard breakpoints (mobile-first)
  xs: 375,        // min-width for small phones
  sm: 480,        // min-width for large phones
  md: 640,        // min-width for tablets portrait
  lg: 768,        // min-width for tablets landscape
  xl: 1024,       // min-width for laptops
  '2xl': 1280,    // min-width for desktops
  '3xl': 1536,    // min-width for wide screens

  // Legacy breakpoints (for backward compatibility)
  mobile: 767,    // max-width for mobile
  desktop: 768,   // min-width for desktop
  tablet: 768,    // min-width for tablet
  laptop: 1024,   // min-width for laptop
  wide: 1280,     // min-width for wide screens

  // Layout-specific breakpoints
  mobileNav: 767,           // max-width for mobile nav
  sidebarCollapsedMin: 768, // min-width for collapsed sidebar
  sidebarCollapsedMax: 1023,// max-width for collapsed sidebar
  sidebarFull: 1024,        // min-width for full sidebar
} as const;

/**
 * Sidebar width values in pixels
 * Matches tailwind.config.js spacing configuration
 */
export const SIDEBAR_WIDTH = {
  expanded: 280,  // Default sidebar width
  collapsed: 64,  // Collapsed sidebar width
} as const;

/**
 * Navigation heights in pixels
 */
export const NAV_HEIGHT = {
  header: 64,       // Top header height
  bottomNav: 64,    // Mobile bottom navigation height
} as const;

// ============================================================================
// Types
// ============================================================================

/**
 * Device type based on viewport width
 */
export type DeviceType = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';

/**
 * Legacy device type for backward compatibility
 */
export type LegacyDeviceType = 'mobile' | 'desktop' | 'tablet' | 'laptop' | 'wide';

/**
 * Responsive state interface
 */
export interface ResponsiveState {
  /** Current viewport width */
  width: number;
  /** Current viewport height */
  height: number;

  // New breakpoint states (mobile-first)
  /** Is viewport xs size (>= 375px) */
  isXs: boolean;
  /** Is viewport sm size (>= 480px) */
  isSm: boolean;
  /** Is viewport md size (>= 640px) */
  isMd: boolean;
  /** Is viewport lg size (>= 768px) */
  isLg: boolean;
  /** Is viewport xl size (>= 1024px) */
  isXl: boolean;
  /** Is viewport 2xl size (>= 1280px) */
  is2xl: boolean;
  /** Is viewport 3xl size (>= 1536px) */
  is3xl: boolean;

  // Layout-specific states
  /** Show mobile bottom navigation (< 768px) */
  showMobileNav: boolean;
  /** Show collapsed sidebar (768px - 1023px) */
  showSidebarCollapsed: boolean;
  /** Show full sidebar (>= 1024px) */
  showSidebarFull: boolean;

  // Legacy states (for backward compatibility)
  /** Is viewport mobile size (< 768px) */
  isMobile: boolean;
  /** Is viewport desktop size (>= 768px) */
  isDesktop: boolean;
  /** Is viewport tablet size (>= 768px) */
  isTablet: boolean;
  /** Is viewport laptop size (>= 1024px) */
  isLaptop: boolean;
  /** Is viewport wide size (>= 1280px) */
  isWide: boolean;

  /** Current device type */
  deviceType: DeviceType;
  /** Legacy device type */
  legacyDeviceType: LegacyDeviceType;
}

/**
 * Options for the useResponsive hook
 */
export interface UseResponsiveOptions {
  /** Debounce delay in milliseconds (default: 100) */
  debounceDelay?: number;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get device type from viewport width (new breakpoint system)
 */
function getDeviceType(width: number): DeviceType {
  if (width >= BREAKPOINTS['3xl']) return '3xl';
  if (width >= BREAKPOINTS['2xl']) return '2xl';
  if (width >= BREAKPOINTS.xl) return 'xl';
  if (width >= BREAKPOINTS.lg) return 'lg';
  if (width >= BREAKPOINTS.md) return 'md';
  if (width >= BREAKPOINTS.sm) return 'sm';
  return 'xs';
}

/**
 * Get legacy device type from viewport width (for backward compatibility)
 */
function getLegacyDeviceType(width: number): LegacyDeviceType {
  if (width >= BREAKPOINTS.wide) return 'wide';
  if (width >= BREAKPOINTS.laptop) return 'laptop';
  if (width >= BREAKPOINTS.tablet) return 'tablet';
  if (width >= BREAKPOINTS.desktop) return 'desktop';
  return 'mobile';
}

/**
 * Calculate responsive state from viewport dimensions
 */
function calculateResponsiveState(width: number, height: number): ResponsiveState {
  return {
    width,
    height,

    // New breakpoint states (mobile-first)
    isXs: width >= BREAKPOINTS.xs,
    isSm: width >= BREAKPOINTS.sm,
    isMd: width >= BREAKPOINTS.md,
    isLg: width >= BREAKPOINTS.lg,
    isXl: width >= BREAKPOINTS.xl,
    is2xl: width >= BREAKPOINTS['2xl'],
    is3xl: width >= BREAKPOINTS['3xl'],

    // Layout-specific states
    showMobileNav: width <= BREAKPOINTS.mobileNav,
    showSidebarCollapsed: width >= BREAKPOINTS.sidebarCollapsedMin && width <= BREAKPOINTS.sidebarCollapsedMax,
    showSidebarFull: width >= BREAKPOINTS.sidebarFull,

    // Legacy states (for backward compatibility)
    isMobile: width <= BREAKPOINTS.mobile,
    isDesktop: width >= BREAKPOINTS.desktop,
    isTablet: width >= BREAKPOINTS.tablet,
    isLaptop: width >= BREAKPOINTS.laptop,
    isWide: width >= BREAKPOINTS.wide,

    deviceType: getDeviceType(width),
    legacyDeviceType: getLegacyDeviceType(width),
  };
}

/**
 * Get initial responsive state (SSR-safe)
 */
function getInitialState(): ResponsiveState {
  if (typeof window === 'undefined') {
    // SSR: Default to desktop
    return calculateResponsiveState(BREAKPOINTS.desktop, 768);
  }
  return calculateResponsiveState(window.innerWidth, window.innerHeight);
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook for responsive design based on TOSS breakpoints
 *
 * @param options - Hook configuration options
 * @returns Current responsive state
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isMobile, isDesktop, deviceType } = useResponsive();
 *
 *   return (
 *     <div>
 *       {isMobile ? (
 *         <MobileLayout />
 *       ) : (
 *         <DesktopLayout />
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function useResponsive(options: UseResponsiveOptions = {}): ResponsiveState {
  const { debounceDelay = 100 } = options;

  const [state, setState] = useState<ResponsiveState>(getInitialState);

  const handleResize = useCallback(() => {
    const newState = calculateResponsiveState(window.innerWidth, window.innerHeight);
    setState(newState);
  }, []);

  useEffect(() => {
    // Initial calculation
    handleResize();

    // Debounced resize handler
    let timeoutId: ReturnType<typeof setTimeout>;

    const debouncedResize = (): void => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, debounceDelay);
    };

    window.addEventListener('resize', debouncedResize);

    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(timeoutId);
    };
  }, [handleResize, debounceDelay]);

  return state;
}

// ============================================================================
// Convenience Hooks
// ============================================================================

/**
 * Hook that returns true only on mobile viewport
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const isMobile = useIsMobile();
 *   return isMobile ? <MobileView /> : <DesktopView />;
 * }
 * ```
 */
export function useIsMobile(): boolean {
  const { isMobile } = useResponsive();
  return isMobile;
}

/**
 * Hook that returns true only on desktop viewport (>= 550px)
 *
 * @example
 * ```tsx
 * function Sidebar() {
 *   const isDesktop = useIsDesktop();
 *   if (!isDesktop) return null;
 *   return <SidebarContent />;
 * }
 * ```
 */
export function useIsDesktop(): boolean {
  const { isDesktop } = useResponsive();
  return isDesktop;
}

/**
 * Hook that returns the current device type
 *
 * @example
 * ```tsx
 * function Layout() {
 *   const deviceType = useDeviceType();
 *   return <div data-device={deviceType}>...</div>;
 * }
 * ```
 */
export function useDeviceType(): DeviceType {
  const { deviceType } = useResponsive();
  return deviceType;
}

/**
 * Hook that returns layout-specific states for sidebar and navigation
 *
 * @example
 * ```tsx
 * function Layout() {
 *   const { showMobileNav, showSidebarCollapsed, showSidebarFull } = useLayoutState();
 *   return (
 *     <div>
 *       {showSidebarFull && <Sidebar />}
 *       {showSidebarCollapsed && <CollapsedSidebar />}
 *       {showMobileNav && <MobileNav />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useLayoutState(): {
  showMobileNav: boolean;
  showSidebarCollapsed: boolean;
  showSidebarFull: boolean;
} {
  const { showMobileNav, showSidebarCollapsed, showSidebarFull } = useResponsive();
  return { showMobileNav, showSidebarCollapsed, showSidebarFull };
}

/**
 * Hook that returns true when sidebar should be visible (either collapsed or full)
 */
export function useShowSidebar(): boolean {
  const { showSidebarCollapsed, showSidebarFull } = useResponsive();
  return showSidebarCollapsed || showSidebarFull;
}

/**
 * Hook that returns true when we're at a large viewport (>= lg / 768px)
 */
export function useIsLargeViewport(): boolean {
  const { isLg } = useResponsive();
  return isLg;
}

/**
 * Hook that returns true when we're at an extra large viewport (>= xl / 1024px)
 */
export function useIsXLargeViewport(): boolean {
  const { isXl } = useResponsive();
  return isXl;
}

/**
 * Hook that returns current viewport dimensions
 *
 * @example
 * ```tsx
 * function Component() {
 *   const { width, height } = useViewportSize();
 *   return <div>Viewport: {width}x{height}</div>;
 * }
 * ```
 */
export function useViewportSize(): { width: number; height: number } {
  const { width, height } = useResponsive();
  return { width, height };
}

// ============================================================================
// Media Query Hook
// ============================================================================

/**
 * Hook for custom media query matching
 *
 * @param query - CSS media query string
 * @returns Whether the media query matches
 *
 * @example
 * ```tsx
 * function Component() {
 *   const isLandscape = useMediaQuery('(orientation: landscape)');
 *   const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
 *
 *   return (
 *     <div className={prefersReducedMotion ? 'no-animation' : ''}>
 *       {isLandscape ? <LandscapeView /> : <PortraitView />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);

    const handler = (event: MediaQueryListEvent): void => {
      setMatches(event.matches);
    };

    mediaQuery.addEventListener('change', handler);

    return () => {
      mediaQuery.removeEventListener('change', handler);
    };
  }, [query]);

  return matches;
}

// ============================================================================
// Default Export
// ============================================================================

export default useResponsive;
